import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Users, UserCircle, Crown, Trash, PaperPlaneTilt, FolderSimple, Check, Warning, Sparkle } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkspacePage() {
  const [workspaces, setWorkspaces] = useState([]);
  const [activeWs, setActiveWs] = useState(null);
  const [wsDetail, setWsDetail] = useState(null);
  const [folders, setFolders] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [newFolder, setNewFolder] = useState('');
  const [msg, setMsg] = useState({ type: '', text: '' });

  const loadWorkspaces = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/workspaces`); setWorkspaces(data); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadWorkspaces(); }, [loadWorkspaces]);

  const loadDetail = useCallback(async (id) => {
    try {
      const [ws, fld] = await Promise.all([
        axios.get(`${API}/workspaces/${id}`),
        axios.get(`${API}/workspaces/${id}/folders`)
      ]);
      setWsDetail(ws.data);
      setFolders(fld.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { if (activeWs) loadDetail(activeWs); }, [activeWs, loadDetail]);

  const createWs = async () => {
    if (!newName.trim()) return;
    try {
      const { data } = await axios.post(`${API}/workspaces`, { name: newName });
      setNewName(''); setShowCreate(false);
      loadWorkspaces();
      setActiveWs(data.id);
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed' }); }
  };

  const invite = async () => {
    if (!inviteEmail.trim() || !activeWs) return;
    setMsg({ type: '', text: '' });
    try {
      const { data } = await axios.post(`${API}/workspaces/${activeWs}/invite`, { email: inviteEmail, role: inviteRole });
      setMsg({ type: 'success', text: data.message });
      setInviteEmail('');
      loadDetail(activeWs);
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed' }); }
  };

  const removeMember = async (userId) => {
    try {
      await axios.delete(`${API}/workspaces/${activeWs}/members/${userId}`);
      loadDetail(activeWs);
    } catch (e) { setMsg({ type: 'error', text: e.response?.data?.detail || 'Failed' }); }
  };

  const changeRole = async (userId, role) => {
    try {
      await axios.patch(`${API}/workspaces/${activeWs}/members/${userId}`, { role });
      loadDetail(activeWs);
    } catch (e) { console.error(e); }
  };

  const createFolder = async () => {
    if (!newFolder.trim() || !activeWs) return;
    try {
      await axios.post(`${API}/workspaces/${activeWs}/folders`, { name: newFolder });
      setNewFolder('');
      loadDetail(activeWs);
    } catch (e) { console.error(e); }
  };

  return (
    <div className="min-h-screen bg-transparent" data-testid="workspace-page">
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Link to="/app" className="w-8 h-8 rounded-lg flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-all" data-testid="workspace-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-xl font-semibold tracking-tight" style={{ fontFamily: 'Clash Display, sans-serif' }}>Teams & Workspaces</h1>
            <p className="text-sm text-white/40">Collaborate with your team</p>
          </div>
          <button onClick={() => setShowCreate(!showCreate)} className="px-4 py-2 bg-lime-400/100 hover:bg-lime-600 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1.5" data-testid="create-workspace-button">
            <Plus className="w-3.5 h-3.5" weight="bold" /> New workspace
          </button>
        </div>

        {/* Create form */}
        <AnimatePresence>
          {showCreate && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden mb-6">
              <div className="border border-white/10 rounded-xl p-5 flex gap-2">
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Workspace name" className="h-10 rounded-lg border-white/10 text-sm flex-1" data-testid="workspace-name-input"
                  onKeyDown={e => { if (e.key === 'Enter') createWs(); }} />
                <button onClick={createWs} className="px-4 h-10 bg-lime-400/100 hover:bg-lime-600 text-white text-sm font-medium rounded-lg transition-colors" data-testid="create-workspace-submit">Create</button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Workspace list */}
        {workspaces.length === 0 && !showCreate && (
          <div className="text-center py-16 border border-dashed border-white/10 rounded-xl">
            <Users weight="duotone" className="w-10 h-10 text-white/60 mx-auto mb-3" />
            <p className="text-sm text-white/50 mb-1">No workspaces yet</p>
            <p className="text-xs text-white/40">Create one to start collaborating with your team</p>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          {workspaces.map(ws => (
            <button key={ws.id} onClick={() => setActiveWs(ws.id)} data-testid={`workspace-card-${ws.id}`}
              className={`text-left p-4 rounded-xl border transition-all ${activeWs === ws.id ? 'border-lime-400/40 bg-lime-400/10' : 'border-white/10 hover:border-white/25'}`}>
              <div className="flex items-center gap-2 mb-1">
                <Users weight="duotone" className="w-4 h-4 text-lime-400" />
                <span className="text-sm font-medium">{ws.name}</span>
              </div>
              <p className="text-xs text-white/40 capitalize">Role: {ws.my_role}</p>
            </button>
          ))}
        </div>

        {/* Workspace detail */}
        {wsDetail && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
            {/* Members */}
            <div className="border border-white/10 rounded-xl p-6">
              <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                <Users weight="duotone" className="w-4 h-4 text-lime-400" /> Members ({wsDetail.members?.length || 0})
              </h3>
              <div className="space-y-2 mb-4">
                {(wsDetail.members || []).map(m => (
                  <div key={m.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/10">
                    <div className="w-8 h-8 rounded-lg bg-lime-400/10 border border-lime-400/20 flex items-center justify-center">
                      <UserCircle weight="duotone" className="w-4 h-4 text-lime-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{m.name || m.email}</p>
                      <p className="text-xs text-white/40">{m.email}</p>
                    </div>
                    <span className={`text-[10px] font-medium uppercase tracking-wide px-2 py-0.5 rounded ${m.role === 'admin' ? 'bg-lime-400/15 text-lime-300' : 'bg-white/10 text-white/50'}`}>
                      {m.role === 'admin' && <Crown weight="fill" className="w-2.5 h-2.5 inline mr-0.5" />}{m.role}
                    </span>
                    {wsDetail.my_role === 'admin' && m.user_id !== wsDetail.owner_id && (
                      <div className="flex items-center gap-1">
                        <select value={m.role} onChange={e => changeRole(m.user_id, e.target.value)} className="text-[10px] px-1 py-0.5 rounded border border-white/10">
                          <option value="member">Member</option>
                          <option value="admin">Admin</option>
                        </select>
                        <button onClick={() => removeMember(m.user_id)} className="w-6 h-6 rounded flex items-center justify-center text-white/40 hover:text-red-400 hover:bg-red-400/15">
                          <Trash className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {/* Invite */}
              {wsDetail.my_role === 'admin' && (
                <div className="flex gap-2 pt-3 border-t border-white/10">
                  <Input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="Email to invite" className="h-9 text-sm rounded-lg border-white/10 flex-1" data-testid="invite-email-input" />
                  <select value={inviteRole} onChange={e => setInviteRole(e.target.value)} className="text-xs px-2 rounded-lg border border-white/10" data-testid="invite-role-select">
                    <option value="member">Member</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button onClick={invite} className="px-3 h-9 bg-lime-400/100 hover:bg-lime-600 text-white text-xs font-medium rounded-lg transition-colors flex items-center gap-1" data-testid="invite-submit">
                    <PaperPlaneTilt className="w-3 h-3" /> Invite
                  </button>
                </div>
              )}
              {msg.text && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`text-xs mt-2 px-3 py-1.5 rounded-lg flex items-center gap-1 ${msg.type === 'success' ? 'bg-lime-400/10 text-lime-300' : 'bg-red-400/10 text-red-400'}`}>
                  {msg.type === 'success' ? <Check weight="bold" className="w-3 h-3" /> : <Warning weight="bold" className="w-3 h-3" />} {msg.text}
                </motion.p>
              )}
            </div>

            {/* Folders */}
            <div className="border border-white/10 rounded-xl p-6">
              <h3 className="text-base font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Clash Display, sans-serif' }}>
                <FolderSimple weight="duotone" className="w-4 h-4 text-lime-400" /> Folders ({folders.length})
              </h3>
              <div className="space-y-1.5 mb-3">
                {folders.map(f => (
                  <div key={f.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5" data-testid={`folder-${f.id}`}>
                    <FolderSimple weight="duotone" className="w-4 h-4 text-lime-500" />
                    <span className="text-sm">{f.name}</span>
                  </div>
                ))}
                {folders.length === 0 && <p className="text-xs text-white/40 px-3">No folders yet</p>}
              </div>
              <div className="flex gap-2">
                <Input value={newFolder} onChange={e => setNewFolder(e.target.value)} placeholder="New folder name" className="h-9 text-sm rounded-lg border-white/10 flex-1" data-testid="folder-name-input"
                  onKeyDown={e => { if (e.key === 'Enter') createFolder(); }} />
                <button onClick={createFolder} className="px-3 h-9 bg-white/10 hover:bg-white/15 text-white/80 text-xs font-medium rounded-lg transition-colors" data-testid="create-folder-submit">Add folder</button>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
